import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);
const IDLE_MS = 15 * 60 * 1000; // 15 min auto-logout

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=logged out, object=logged in
  const idleTimer = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  }, []);

  // idle auto-logout
  useEffect(() => {
    if (!user) return;
    const reset = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => { logout(); }, IDLE_MS);
    };
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [user, logout]);

  const value = { user, setUser, checkAuth, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

const ALL = ["pos", "products", "inventory", "orders", "invoices", "payments", "shipping", "dashboard", "customers", "employees", "reports", "store", "ai", "settings", "refund"];
const PERMISSIONS = {
  owner: ALL,
  // Manager = everything except employee management
  manager: ALL.filter((p) => p !== "employees"),
  // Cashier = POS (incl. customer pick/create) / AI (+ store & settings for own 2FA)
  cashier: ["pos", "ai", "store", "settings"],
  // Inventory Staff = products (view), inventory, shipping
  inventory: ["products", "inventory", "shipping", "store", "ai", "settings"],
  // Sales Staff = POS, orders, online store, AI (customer pages are owner/manager only)
  sales: ["pos", "orders", "store", "ai", "settings"],
};

export function can(role, perm) {
  return (PERMISSIONS[role] || []).includes(perm);
}
